import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Heart, Waves, Zap } from 'lucide-react';

interface GuidedMeditationProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (notes?: string) => void;
}

type MeditationType = 'calm' | 'energy' | 'focus';

interface MeditationConfig {
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  breathingPattern: {
    inhale: number;
    hold: number;
    exhale: number;
    pause: number;
  };
  instructions: string[];
  color: string;
  gradient: string;
}

const meditations: Record<MeditationType, MeditationConfig> = {
  calm: {
    name: 'Calming Breath',
    icon: Heart,
    description: 'Slow, deep breathing to relax and unwind',
    breathingPattern: { inhale: 4, hold: 2, exhale: 6, pause: 2 },
    instructions: [
      'Find a comfortable position and close your eyes',
      'Let your shoulders drop and relax your jaw',
      'Follow the breathing guide with slow, gentle breaths',
      'Feel tension melting away with each exhale'
    ],
    color: 'text-blue-600',
    gradient: 'from-blue-400 to-blue-600'
  },
  energy: {
    name: 'Energizing Breath',
    icon: Zap,
    description: 'Rhythmic breathing to boost energy and alertness',
    breathingPattern: { inhale: 3, hold: 1, exhale: 3, pause: 1 },
    instructions: [
      'Sit up straight with your spine aligned',
      'Place one hand on your chest, one on your belly',
      'Breathe with purpose and intention',
      'Feel energy flowing through your body'
    ],
    color: 'text-orange-600',
    gradient: 'from-orange-400 to-orange-600'
  },
  focus: {
    name: 'Focused Breath',
    icon: Waves,
    description: 'Balanced breathing to enhance concentration',
    breathingPattern: { inhale: 4, hold: 4, exhale: 4, pause: 4 },
    instructions: [
      'Maintain a steady, upright posture',
      'Focus your attention on the breath',
      'Count each phase of the breathing cycle',
      'Let your mind become clear and focused'
    ],
    color: 'text-purple-600',
    gradient: 'from-purple-400 to-purple-600'
  }
};

const GuidedMeditation: React.FC<GuidedMeditationProps> = ({ isOpen, onClose, onComplete }) => {
  const [selectedMeditation, setSelectedMeditation] = useState<MeditationType | null>(null);
  const [currentPhase, setCurrentPhase] = useState<'inhale' | 'hold' | 'exhale' | 'pause'>('inhale');
  const [timer, setTimer] = useState(60);
  const [phaseTimer, setPhaseTimer] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [notes, setNotes] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const phaseIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMeditation(null);
      setTimer(60);
      setIsActive(false);
      setShowForm(false);
      setNotes('');
      setCurrentPhase('inhale');
      setPhaseTimer(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isActive && timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsActive(false);
      setShowForm(true);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, timer]);

  useEffect(() => {
    if (isActive && selectedMeditation) {
      const pattern = meditations[selectedMeditation].breathingPattern;
      const phases: Array<{ phase: 'inhale' | 'hold' | 'exhale' | 'pause'; duration: number }> = [
        { phase: 'inhale', duration: pattern.inhale },
        { phase: 'hold', duration: pattern.hold },
        { phase: 'exhale', duration: pattern.exhale },
        { phase: 'pause', duration: pattern.pause }
      ];

      let currentPhaseIndex = 0;
      let phaseTimeLeft = phases[0].duration;

      setCurrentPhase(phases[0].phase);
      setPhaseTimer(phaseTimeLeft);

      phaseIntervalRef.current = setInterval(() => {
        phaseTimeLeft -= 1;
        setPhaseTimer(phaseTimeLeft);

        if (phaseTimeLeft <= 0) {
          currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
          phaseTimeLeft = phases[currentPhaseIndex].duration;
          setCurrentPhase(phases[currentPhaseIndex].phase);
          setPhaseTimer(phaseTimeLeft);
        }
      }, 1000);

      return () => {
        if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
      };
    }
  }, [isActive, selectedMeditation]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const startMeditation = (type: MeditationType) => {
    setSelectedMeditation(type);
    setIsActive(true);
  };

  const skipTimer = () => {
    setTimer(0);
    setIsActive(false);
    setShowForm(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
  };

  const handleSubmit = () => {
    onComplete(notes || undefined);
  };

  const getPhaseInstruction = () => {
    switch (currentPhase) {
      case 'inhale': return 'Breathe In';
      case 'hold': return 'Hold';
      case 'exhale': return 'Breathe Out';
      case 'pause': return 'Pause';
    }
  };

  const getBreathingCircleScale = () => {
    if (!selectedMeditation) return 1;
    
    const pattern = meditations[selectedMeditation].breathingPattern;
    const totalCycle = pattern.inhale + pattern.hold + pattern.exhale + pattern.pause;
    const progress = (totalCycle - phaseTimer) / totalCycle;

    switch (currentPhase) {
      case 'inhale':
        return 1 + (progress * 0.5);
      case 'hold':
        return 1.5;
      case 'exhale':
        return 1.5 - (progress * 0.5);
      case 'pause':
        return 1;
      default:
        return 1;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isActive) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-xl shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                {!selectedMeditation ? 'Choose Your Meditation' : 
                 !showForm ? 'Guided Meditation' : 'Complete Your Meditation'}
              </h2>
              {!isActive && (
                <button 
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {!selectedMeditation ? (
              <div className="space-y-4">
                {(Object.keys(meditations) as MeditationType[]).map((type) => {
                  const meditation = meditations[type];
                  const Icon = meditation.icon;
                  
                  return (
                    <motion.button
                      key={type}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => startMeditation(type)}
                      className="w-full p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full bg-gradient-to-br ${meditation.gradient}`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-1">{meditation.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{meditation.description}</p>
                          <div className="text-xs text-gray-500">
                            {meditation.breathingPattern.inhale}s in • {meditation.breathingPattern.hold}s hold • {meditation.breathingPattern.exhale}s out • {meditation.breathingPattern.pause}s pause
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            ) : !showForm ? (
              <div className="text-center">
                <div className="mb-6">
                  <div className="relative w-32 h-32 mx-auto mb-4">
                    <motion.div
                      animate={{ scale: getBreathingCircleScale() }}
                      transition={{ duration: 1, ease: "easeInOut" }}
                      className={`w-full h-full rounded-full bg-gradient-to-br ${meditations[selectedMeditation].gradient} flex items-center justify-center`}
                    >
                      <div className="text-white text-center">
                        <div className="text-lg font-medium mb-1">
                          {getPhaseInstruction()}
                        </div>
                        <div className="text-sm opacity-80">
                          {phaseTimer}s
                        </div>
                      </div>
                    </motion.div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-2xl font-medium text-purple-600">
                      {formatTime(timer)}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-800 mb-2">{meditations[selectedMeditation].name}</h3>
                    <div className="space-y-1">
                      {meditations[selectedMeditation].instructions.map((instruction, index) => (
                        <p key={index} className="text-sm text-gray-600">
                          {instruction}
                        </p>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center text-gray-500 mb-2">
                    <Clock size={16} className="mr-1" />
                    <span className="text-sm">60 seconds</span>
                  </div>
                </div>

                <button
                  onClick={skipTimer}
                  className="text-purple-600 hover:text-purple-800 transition-colors text-sm"
                >
                  Skip to meditation notes
                </button>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">
                  How was your meditation? Note down any insights or feelings from your practice.
                </p>
                
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors mb-4"
                  rows={4}
                  placeholder="My meditation insights..."
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors flex-1"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex-1"
                  >
                    Complete
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GuidedMeditation;